import { action, makeObservable, observable } from 'mobx';
import { gameModuleService } from '../services/gameModuleService';
import { GAME_SPEED_TYPES } from '../../constants/gameConstants';
import { gameGlobalStates } from '../../constants/reelConstants';

class GameModuleStore {
  initialMatrix = [];
  resultMatrix = [];
  doSpin = null;
  doRespin = null;
  soundService = null;
  animationEnd = null;
  gameGlobalState = gameGlobalStates.none;
  spinSpeedType = 'standard';
  spinDirection = 1;
  additional = null;
  isForceStop = false;
  awaitForceStop = false;
  canTouchControl = true;
  winAnimations = [];
  responseReady = false;
  isIntro = false;
  wildIcons = [];
  // RU: идёт серия респинов — барабаны с раскрытой звездой стоят, раунд ещё не закрыт.
  retriggerMode = false;
  // RU: незаконченная серия респинов из прошлой сессии (перезагрузка посреди неё) — продолжается,
  //     когда барабаны созданы (gameModuleService.createReels).
  pendingRespin = null;

  constructor() {
    makeObservable(this, {
      initialMatrix: observable,
      resultMatrix: observable,
      isIntro: observable,
      additional: observable,
      doSpin: observable,
      soundService: observable,
      animationEnd: observable,
      gameGlobalState: observable,
      spinSpeedType: observable,
      spinDirection: observable,
      isForceStop: observable,
      awaitForceStop: observable,
      canTouchControl: observable,
      winAnimations: observable,
      responseReady: observable,
      getPropsFromFrame: action.bound,
      setResultMatrix: action.bound,
      setSpinSpeedType: action.bound,
      setSpinDirection: action.bound,
      handleReelAnimationEnd: action.bound,
      finishRoundWithoutWin: action.bound,
      setResponseReady: action.bound,
      setAdditional: action.bound,
      setIsForceStop: action.bound,
      setGameGlobalState: action.bound,
      setCanTouchControl: action.bound,
      setWinAnimations: action.bound,
      setAwaitForceStop: action.bound,
      setWildIcons: action.bound,
    });
  }

  // RU: `isIntro` приходит только детям интро (introChildrenProps); у этой игры интро нет, и
  //     проп остаётся undefined — поле нужно общим хелперам (свайп, терминал показа линий).
  //     `result` ядро 3.x отдаёт после ЛЮБОГО прошлого спина сессии; серию респинов продолжаем,
  //     только если прошлый раунд её не закончил (`retrigger`).
  getPropsFromFrame = ({ initialMatrix, doSpin, doRespin, soundService, animationEnd, isIntro, result }) => {
    this.pendingRespin = result?.retrigger ? result : null;
    this.initialMatrix = initialMatrix;
    this.doSpin = doSpin;
    this.doRespin = doRespin;
    this.soundService = soundService;
    this.animationEnd = animationEnd;
    this.isIntro = Boolean(isIntro);
  };

  setSpinSpeedType = (type) => {
    this.spinSpeedType = type;
  };

  setGameGlobalState = (state) => {
    this.gameGlobalState = state;
  };

  setSpinDirection = (direction) => {
    this.spinDirection = direction;
  };

  // RU: `retrigger` — выпала звезда: её барабан раскрывается и стоит, остальные уходят в респин
  //     (раунд не закрывается). Иначе — показ выигрыша или проигрыш; пустой `wins` — проигрыш
  //     (`[]` истинно, и прежде доска уходила в показ выигрыша без единой линии).
  handleReelAnimationEnd = () => {
    const { retrigger, wins, expandingStars } = this.additional || {};
    const hasWins = Boolean(wins?.length);
    this.setGameGlobalState(!retrigger && hasWins ? gameGlobalStates.show : gameGlobalStates.none);
    if (retrigger) {
      this.retriggerMode = true;
      gameModuleService.startRespin(expandingStars, hasWins ? wins : []);
    } else {
      if (this.retriggerMode) {
        this.retriggerMode = false;
        gameModuleService.clearRetriggerState();
      }
      if (hasWins) gameModuleService.startWinAnimation(wins);
      else this.finishRoundWithoutWin();
    }
    this.setAwaitForceStop(false);
    this.setIsForceStop(false);
    this.setResponseReady(false);
  };

  // RU: свайп поднимается ДО отчёта ядру: на `noWin` ядро может сразу запустить следующий спин
  //     (автоигра), и его `startSpin` должен опустить флаг последним.
  finishRoundWithoutWin = () => {
    this.setCanTouchControl(true);
    this.animationEnd('noWin');
  };

  setResponseReady = (status) => {
    this.responseReady = status;
  };

  setAdditional = (additional) => {
    this.additional = additional;
  };

  // RU: гард — только на ВЗВОД. В турбо барабаны и так идут мимо, а повторный взвод не нужен:
  //     барабаны реагируют на смену значения. Прежде гард стоял и на сброс — и
  //     `setIsForceStop(false)` при поднятом флаге не делал ничего.
  setIsForceStop = (status) => {
    if (status && (this.spinSpeedType === GAME_SPEED_TYPES.turbo || this.isForceStop)) return;
    this.isForceStop = status;
  };

  setAwaitForceStop = (status) => {
    this.awaitForceStop = status;
  };

  setCanTouchControl = (status) => {
    this.canTouchControl = status;
  };

  setWinAnimations = (animation) => {
    if (animation) this.winAnimations.push(animation);
    else this.winAnimations = [];
  };

  setResultMatrix = (matrix) => {
    this.resultMatrix = matrix;
  };

  setWildIcons = (icon) => {
    if (icon) this.wildIcons.push(icon);
    else this.wildIcons = [];
  };
}

export const gameModuleStore = new GameModuleStore();
